import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useMyRoles } from "@/hooks/useMyRoles";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AdminMemberList from "@/components/admin/AdminMemberList";
import AdminEventList from "@/components/admin/AdminEventList";
import AdminReferralList from "@/components/admin/AdminReferralList";
import AdminVisitRequestList from "@/components/admin/AdminVisitRequestList";
import AdminGivingList from "@/components/admin/AdminGivingList";
import AdminReferralStats from "@/components/admin/AdminReferralStats";
import AdminApplicationList from "@/components/admin/AdminApplicationList";
import AdminRolesList from "@/components/admin/AdminRolesList";
import AdminEmailDashboard from "@/components/admin/AdminEmailDashboard";
import AdminSpeakerSchedule from "@/components/admin/AdminSpeakerSchedule";
import AdminRenewalReminders from "@/components/admin/AdminRenewalReminders";
import AdminPayments from "@/components/admin/AdminPayments";
import { Shield, ArrowLeft, BarChart3, Users, Calendar, Mic, Send, MapPin, Heart, Mail, CalendarClock, CreditCard, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";


const Admin = () => {
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const { isAdmin, has, hasAnyAdminRole, loading: adminLoading } = useMyRoles();
  const navigate = useNavigate();


  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
      setAuthLoading(false);
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUser(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!hasAnyAdminRole) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">You don't have admin permissions to view this page.</p>
            <Button onClick={() => navigate("/portal")}>Go to Member Portal</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const allTabItems = [
    { value: "reports", label: "Reports", Icon: BarChart3, allowed: isAdmin },
    { value: "applications", label: "Applications", Icon: Users, allowed: has("applicant_admin") },
    { value: "members", label: "Members", Icon: Users, allowed: has("member_admin") },
    { value: "events", label: "Events", Icon: Calendar, allowed: has("events_admin") },
    { value: "speakers", label: "Speakers", Icon: Mic, allowed: has("events_admin") },
    { value: "referrals", label: "Referrals", Icon: Send, allowed: isAdmin },
    { value: "visits", label: "Visits", Icon: MapPin, allowed: has("visit_admin") },
    { value: "giving", label: "Giving", Icon: Heart, allowed: isAdmin },
    { value: "emails", label: "Emails", Icon: Mail, allowed: isAdmin },
    { value: "renewals", label: "Renewals", Icon: CalendarClock, allowed: isAdmin },
    { value: "payments", label: "Payments", Icon: CreditCard, allowed: isAdmin },
    { value: "roles", label: "Roles", Icon: ShieldCheck, allowed: isAdmin },
  ];

  const tabItems = allTabItems.filter((t) => t.allowed);
  const allowedValues = new Set(tabItems.map((t) => t.value));
  const currentTab = activeTab && allowedValues.has(activeTab) ? activeTab : tabItems[0]?.value ?? "";
  const gridColsClass: Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    7: "grid-cols-7",
    8: "grid-cols-8",
    9: "grid-cols-9",
    10: "grid-cols-10",
    11: "grid-cols-11",
    12: "grid-cols-12",
  };


  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 pt-20 md:pt-36 lg:pt-20 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-3xl font-bold">Admin Dashboard</h1>
                <p className="text-muted-foreground">Manage members and events across all chapters</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/portal")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Member Portal
            </Button>
          </div>

          <Tabs value={currentTab} onValueChange={setActiveTab} className="w-full">
            <div className="lg:hidden">
              <Select value={currentTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full h-12 text-base" aria-label="Select admin section">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tabItems.map(({ value, label, Icon }) => (
                    <SelectItem key={value} value={value}>
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0" />
                        {label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="hidden lg:block">
              <TabsList className={`grid w-full ${gridColsClass[tabItems.length] ?? "grid-cols-12"}`}>
                {tabItems.map(({ value, label, Icon }) => (
                  <TabsTrigger key={value} value={value} className="flex items-center gap-1.5 whitespace-nowrap">
                    <Icon className="h-4 w-4 shrink-0" />
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            {allowedValues.has("reports") && (
              <TabsContent value="reports" className="mt-6">
                <AdminReferralStats />
              </TabsContent>
            )}
            {allowedValues.has("applications") && (
              <TabsContent value="applications" className="mt-6">
                <AdminApplicationList />
              </TabsContent>
            )}
            {allowedValues.has("members") && (
              <TabsContent value="members" className="mt-6">
                <AdminMemberList />
              </TabsContent>
            )}
            {allowedValues.has("events") && (
              <TabsContent value="events" className="mt-6">
                <AdminEventList />
              </TabsContent>
            )}
            {allowedValues.has("speakers") && (
              <TabsContent value="speakers" className="mt-6">
                <AdminSpeakerSchedule />
              </TabsContent>
            )}
            {allowedValues.has("referrals") && (
              <TabsContent value="referrals" className="mt-6">
                <AdminReferralList />
              </TabsContent>
            )}
            {allowedValues.has("visits") && (
              <TabsContent value="visits" className="mt-6">
                <AdminVisitRequestList />
              </TabsContent>
            )}
            {allowedValues.has("giving") && (
              <TabsContent value="giving" className="mt-6">
                <AdminGivingList />
              </TabsContent>
            )}
            {allowedValues.has("emails") && (
              <TabsContent value="emails" className="mt-6">
                <AdminEmailDashboard />
              </TabsContent>
            )}
            {allowedValues.has("renewals") && (
              <TabsContent value="renewals" className="mt-6">
                <AdminRenewalReminders />
              </TabsContent>
            )}
            {allowedValues.has("payments") && (
              <TabsContent value="payments" className="mt-6">
                <AdminPayments />
              </TabsContent>
            )}
            {allowedValues.has("roles") && (
              <TabsContent value="roles" className="mt-6">
                <AdminRolesList />
              </TabsContent>
            )}

          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Admin;
